import type { AppSettings, FileMeta, ParsedDocument } from '../app/types'

export type ParseStage = 'read' | 'parse' | 'render'

export interface ParseContext {
  meta: FileMeta
  settings: AppSettings
  report: (stage: ParseStage, message: string) => void
}

export async function parseFile(file: File, context: ParseContext): Promise<ParsedDocument> {
  context.report('read', '读取文件')

  switch (context.meta.kind) {
    case 'docx': {
      const { parseDocx } = await import('./parseDocx')
      return parseDocx(file, context)
    }
    case 'xlsx': {
      const { parseXlsx } = await import('./parseXlsx')
      return parseXlsx(file, context)
    }
    case 'md': {
      const { parseMarkdown } = await import('./parseMarkdown')
      return parseMarkdown(file, context)
    }
    case 'html': {
      const { parseHtml } = await import('./parseHtml')
      return parseHtml(file, context)
    }
    case 'csv': {
      const { parseCsv } = await import('./parseCsv')
      return parseCsv(file, context)
    }
    case 'json': {
      const { parseJson } = await import('./parseJson')
      return parseJson(file, context)
    }
    case 'lottie': {
      const { parseLottie } = await import('./parseLottie')
      return parseLottie(file, context)
    }
    case 'txt': {
      const { parseText } = await import('./parseText')
      return parseText(file, context)
    }
    case 'pdf': {
      const { parsePdf } = await import('./parsePdf')
      return parsePdf(file, context)
    }
    default:
      throw new Error('暂不支持该格式。支持 DOCX、XLSX、PDF、Markdown、HTML、CSV、JSON、Lottie 和 TXT。')
  }
}

export async function readFileText(file: File): Promise<string> {
  const buffer = await file.arrayBuffer()
  return new TextDecoder('utf-8', { fatal: false }).decode(buffer)
}

export function createBaseDocument(
  context: ParseContext,
  fields: Omit<ParsedDocument, 'id' | 'kind' | 'meta'>,
): ParsedDocument {
  return {
    id: context.meta.id,
    kind: context.meta.kind,
    meta: context.meta,
    ...fields,
    warnings: [...context.meta.warnings, ...fields.warnings],
  }
}
